import { cn } from "../../ui/cn";
import { StatusBadge, type StatusTone } from "../../ui/status-badge";
import type { PlaceBadgeInfo, PlaceTone } from "./state";

const tones: Readonly<Record<PlaceTone, StatusTone>> = {
  ok: "success",
  warn: "warning",
  error: "danger",
  neutral: "neutral",
};

/**
 * A state as a glyph and a word: it never depends on colour alone. Two more cues back the glyph for the two states that most need one: an
 * error has a heavier border, and a state nobody has assessed has a dashed one. The `data-state` is the stable key of the state and the
 * `data-tone` its tone (for tests).
 */
export function PlaceBadge({ badge }: Readonly<{ badge: PlaceBadgeInfo }>) {
  return (
    <span data-state={badge.key} data-tone={badge.tone} className="inline-flex max-w-full">
      {/* A long "Error: ..." label wraps instead of widening the page. */}
      <StatusBadge
        tone={tones[badge.tone]}
        glyph={badge.glyph}
        className={cn(
          "max-w-full whitespace-normal [overflow-wrap:anywhere]",
          badge.tone === "error" && "border-2",
          badge.tone === "neutral" && "border-dashed",
        )}
      >
        {badge.label}
      </StatusBadge>
    </span>
  );
}

/** Only an https address is made a link. Provider text never becomes a script or a relative address. */
export function isHttpsUrl(value: string | null): value is string {
  if (value === null) return false;
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
}
